import React, { useState, useEffect} from 'react';
import { getUILoc, getCardName, groupCards } from '../utils/cardUtils.js';
import { HUMAN_PLAYER_ID, NPC_PLAYER_ID, BGMCategory } from '../gameLogic/constants.js';
import { CardIllustration } from './Card.js';
import CardDetail from './CardDetail.js';
import audioManager from '../utils/AudioManager.js';
import './YonotsuneRewardScreen.css';

const YonotsuneRewardScreen = ({
  yonotsune,
  gameState,
  onReturnToMenu,
  onScreenChange
}) => {
  const {
    activeRun,
    finishRun,
    goToNextNodeSelection,
    generateDraftOptions,
    generateBuffOptions,
    cardDefs
  } = yonotsune;

  const [selectedPattern, setSelectedPattern] = useState(null); // 'pattern1' or 'pattern2'
  const [selectedBuff, setSelectedBuff] = useState(null);
  const [draftOptions, setDraftOptions] = useState(null);
  const [buffOptions, setBuffOptions] = useState([]);
  const [inspectCard, setInspectCard] = useState(null);

  const pendingResult = activeRun?.pendingMatchResult;

  useEffect(() => {
    if (pendingResult) {
      setDraftOptions(pendingResult.draftOptions);
      setBuffOptions(pendingResult.buffOptions || []);
    } else if (gameState?.players?.[NPC_PLAYER_ID]) {
      // フォールバック（通常はApp.js側でrecordMatchResultが呼ばれているはず）
      const npcDiscard = gameState.players[NPC_PLAYER_ID].discard || [];
      const npcDeck = gameState.players[NPC_PLAYER_ID].deck || [];
      const pool = [...npcDiscard, ...npcDeck].map(c => c.id);
      setDraftOptions(generateDraftOptions(pool));
      if (activeRun) {
        setBuffOptions(generateBuffOptions(activeRun.currentPhase));
      }
    }
  }, [pendingResult, gameState, activeRun, generateDraftOptions, generateBuffOptions]);

  const winner = gameState?.winner;
  const isVictory = pendingResult ? pendingResult.isVictory : (winner === HUMAN_PLAYER_ID);

  const handleSelectDraft = (pattern) => {
    setSelectedPattern(pattern);
  };

  const handleSelectBuff = (buff) => {
    setSelectedBuff(buff);
  };

  const handleProceed = () => {
    if (!isVictory) {
      finishRun();
      onReturnToMenu();
      return;
    }

    if (!selectedPattern || !selectedBuff) return;

    // 報酬の一括適用 (進行数カウントアップ等も内部で処理)
    yonotsune.applyBattleRewards(selectedPattern, draftOptions, selectedBuff, true);

    // 画面遷移
    goToNextNodeSelection();
    onScreenChange('yonotsune');
  };

  const handleFinishRun = () => {
    audioManager.ensureCategoryBGM(BGMCategory.YONOTSUNE);
    if (!isVictory && activeRun?.isOpportunityBattle) {
      if (yonotsune.clearOpportunityBattle) yonotsune.clearOpportunityBattle();
      goToNextNodeSelection();
      onScreenChange('yonotsune');
      return;
    }
    finishRun();
    onReturnToMenu();
  };

  // ガード節の修正：gameStateがない（中断復帰）場合でも、pendingResultがあれば表示を続行する
  // また、敗北時は draftOptions が null になるので、それも許容する
  if (!pendingResult && !gameState) return null;
  if (isVictory && !draftOptions) return null;

  // 敗北・クリア画面
  if (!isVictory || activeRun.matchedCount >= 10) {
    const isFullClear = isVictory && activeRun.matchedCount >= 10;
    const groupedFinalDeck = groupCards(activeRun.playerDeck);

    // スコア計算
    const scoreDetails = {
        cards: { label: getUILoc('YONOTSUNE.REWARD_SCREEN.SCORE_CARDS') || '累計獲得枚数', count: activeRun.acquiredCardsCount || 0, rate: 3 },
        matches: { label: getUILoc('YONOTSUNE.REWARD_SCREEN.SCORE_MATCHES') || '総進行数', count: activeRun.matchedCount || 0, rate: 10 },
        friction: { label: getUILoc('YONOTSUNE.REWARD_SCREEN.SCORE_FRICTION') || '文化摩擦勝利数', count: activeRun.frictionWins || 0, rate: 5 },
        con: { label: getUILoc('YONOTSUNE.REWARD_SCREEN.SCORE_CON') || '最終意識ボーナス', count: activeRun.consciousnessBonus || 0, rate: 2 }
    };

    const totalScore = Object.values(scoreDetails).reduce((sum, item) => sum + (item.count * item.rate), 0);

    return (
      <div className={`reward-screen-wrapper ${isFullClear ? 'victory' : 'defeat'}`}>
        <div className="result-banner">
          <h1 className="yonotsune-title">{isFullClear ? getUILoc('YONOTSUNE.REWARD_SCREEN.RUN_COMPLETE') : getUILoc('YONOTSUNE.REWARD_SCREEN.RUN_FAILED')}</h1>
        </div>

        <div className="result-main-container">
            <div className="score-summary-box">
                <h2 className="score-total">SCORE: {totalScore}</h2>
                <div className="score-breakdown">
                    {Object.entries(scoreDetails).map(([key, item]) => (
                        <div key={key} className="score-row">
                            <span className="label">{item.label}</span>
                            <span className="calculation">{item.count} × {item.rate}</span>
                            <span className="value">+{item.count * item.rate}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="result-details-box">
                <div className="result-row">
                    <span className="label">{getUILoc('YONOTSUNE.REWARD_SCREEN.SELECTED_MEMORY') || '挑んだ記憶'}:</span>
                    <span className="value">{activeRun.initialDeckKey ? getUILoc(`YONOTSUNE.INITIAL_DECKS.${activeRun.initialDeckKey}.NAME`) : activeRun.selectedDeckName}</span>
                </div>
                <div className="result-row">
                    <span className="label">{getUILoc('YONOTSUNE.REWARD_SCREEN.FINAL_PHASE')}:</span>
                    <span className="value">{activeRun.currentPhase}</span>
                </div>
                <div className="result-row">
                    <span className="label">{getUILoc('YONOTSUNE.REWARD_SCREEN.COMPLETED_MATCHES')}:</span>
                    <span className="value">{activeRun.matchedCount}</span>
                </div>
                <div className="result-row final-deck-box">
                    <span className="label">{getUILoc('YONOTSUNE.REWARD_SCREEN.FINAL_DECK')}:</span>
                    <div className="deck-chips scrollable">
                    {groupedFinalDeck.map((item, i) => (
                        <div 
                        key={i} 
                        className={`pile-card-group clickable ${item.id === 1000 ? 'art-chip' : ''}`}
                        onClick={() => setInspectCard(cardDefs[item.id])}
                        >
                        <div className="pile-card-illustration-wrapper">
                            <CardIllustration cardId={item.id} isMini={true} />
                            <div className="pile-card-count-badge">×{item.count}</div>
                        </div>
                        <div className="pile-card-name">{getCardName(item.id)}</div>
                        </div>
                    ))}
                    </div>
                </div>
            </div>
        </div>

        <div className="action-row">
          <button className="yonotsune-btn start-btn" onClick={handleFinishRun}>
            {!isVictory && activeRun?.isOpportunityBattle
              ? getUILoc('YONOTSUNE.EVENTS.NEXT_NODE')
              : getUILoc('YONOTSUNE.REWARD_SCREEN.BACK_TO_TITLE')}
          </button>
        </div>
        {inspectCard && (
            <CardDetail card={inspectCard} cardDefs={cardDefs} onClose={() => setInspectCard(null)} />
        )}
      </div>
    );
  }

  // 勝利報酬画面
  return (
    <div className="reward-screen-wrapper victory">
      <div className="result-banner">
        <h1 className="yonotsune-title">{getUILoc('YONOTSUNE.REWARD_SCREEN.VICTORY')}</h1>
      </div>

      <section className="draft-section">
        <h2 className="section-title">{getUILoc('YONOTSUNE.REWARD_SCREEN.DRAFT_TITLE')}</h2>
        <div className="draft-container">
          {['pattern1', 'pattern2'].map((pattern) => {
            const groupedDraft = groupCards(draftOptions[pattern]);
            return (
              <div 
                key={pattern} 
                className={`draft-option-card ${selectedPattern === pattern ? 'active' : ''}`}
                onClick={() => handleSelectDraft(pattern)}
              >
                <div className="card-header">{pattern === 'pattern1' ? getUILoc('YONOTSUNE.REWARD_SCREEN.PATTERN_A') : getUILoc('YONOTSUNE.REWARD_SCREEN.PATTERN_B')}</div>
                <div className="draft-cards-list">
                  {groupedDraft.map((item, i) => (
                      <div key={i} className="pile-card-group clickable" onClick={(e) => { e.stopPropagation(); setInspectCard(cardDefs[item.id]); }}>
                        <div className="pile-card-illustration-wrapper">
                          <CardIllustration cardId={item.id} isMini={true} />
                          <div className="pile-card-count-badge">×{item.count}</div>
                        </div>
                        <div className="pile-card-name">{getCardName(item.id)}</div>
                      </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="buff-section">
        <h2 className="section-title">{getUILoc('YONOTSUNE.REWARD_SCREEN.BUFF_TITLE')}</h2>
        <div className="buff-container">
          {buffOptions.map((buff) => {
            const canApply = (15 + activeRun.consciousnessBonus + buff.effect.con >= 0) &&
                             (5 + activeRun.scaleBonus + buff.effect.scale >= 0);
            return (
              <div 
                key={buff.id}
                className={`buff-option-card ${selectedBuff?.id === buff.id ? 'active' : ''} ${!canApply ? 'disabled' : ''}`}
                onClick={() => canApply && handleSelectBuff(buff)}
              >
                <div className="buff-header">{buff.desc || buff.id}</div>
                <div className="buff-stats-preview">
                  {buff.effect.con !== 0 && `${getUILoc('LABELS.CONSCIOUSNESS')}: ${buff.effect.con > 0 ? '+' : ''}${buff.effect.con} `}
                  {buff.effect.scale !== 0 && `${getUILoc('LABELS.SCALE')}: ${buff.effect.scale > 0 ? '+' : ''}${buff.effect.scale}`}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="action-row">
        <button 
          className="yonotsune-btn proceed-btn" 
          disabled={!selectedPattern || !selectedBuff}
          onClick={handleProceed}
        >
          {getUILoc('YONOTSUNE.REWARD_SCREEN.CONTINUE')}
        </button>
      </div>

      {inspectCard && (
        <CardDetail 
          card={inspectCard} 
          cardDefs={cardDefs} 
          onClose={() => setInspectCard(null)} 
        />
      )}
    </div>
  );
};

export default YonotsuneRewardScreen;
